/**
 * `/v1/ops/directory/ops-users` — a minimal ops-user directory for label
 * enrichment on non-admin pages. Five fields only; roles, GitHub and Feishu
 * identifiers stay on the admin-only `/v1/ops/ops-users`.
 */

export interface OpsDirectoryUser {
  readonly id: string;
  readonly email: string;
  /** Server-resolved, never empty (same chain as OpsUserRecord.display_name). */
  readonly display_name: string;
  readonly feishu_name: string | null;
  readonly disabled_at: string | null;
}

export interface ListOpsDirectoryUsersResponse {
  readonly users: readonly OpsDirectoryUser[];
}

/**
 * `/v1/ops/directory/customer-companies` — batch company-name enrichment for
 * ops-console customer cells (#3040). `company` is `customer_contacts.company`
 * trimmed; a missing card or a blank value is null. Every requested id gets
 * exactly one entry, in request order; an unknown id comes back with nulls.
 */
export const CUSTOMER_COMPANY_LOOKUP_MAX_IDS = 500;

export const CUSTOMER_COMPANY_LOOKUP_MAX_ID_LENGTH = 200;

export interface CustomerCompanyLookupRequest {
  readonly org_ids?: readonly string[];
  readonly account_ids?: readonly string[];
}

export interface CustomerCompanyOrgEntry {
  readonly org_id: string;
  readonly company: string | null;
}

export interface CustomerCompanyAccountEntry {
  readonly account_id: string;
  readonly org_id: string | null;
  readonly company: string | null;
}

export interface CustomerCompanyLookupResponse {
  readonly orgs: readonly CustomerCompanyOrgEntry[];
  readonly accounts: readonly CustomerCompanyAccountEntry[];
}

function isLookupRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function parseIdList(raw: unknown): string[] | null {
  if (raw === undefined) {
    return [];
  }
  if (!Array.isArray(raw)) {
    return null;
  }
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const value of raw) {
    if (typeof value !== 'string') {
      return null;
    }
    const id = value.trim();
    if (id.length === 0 || id.length > CUSTOMER_COMPANY_LOOKUP_MAX_ID_LENGTH) {
      return null;
    }
    if (!seen.has(id)) {
      ids.push(id);
      seen.add(id);
    }
  }
  return ids;
}

/**
 * Normalize a lookup body. Returns null when the payload is malformed, when
 * both lists are empty after de-duplication, or when the de-duplicated total
 * exceeds {@link CUSTOMER_COMPANY_LOOKUP_MAX_IDS}.
 */
export function parseCustomerCompanyLookupRequest(
  raw: unknown,
): { org_ids: string[]; account_ids: string[] } | null {
  if (!isLookupRecord(raw)) {
    return null;
  }
  const orgIds = parseIdList(raw.org_ids);
  const accountIds = parseIdList(raw.account_ids);
  if (orgIds === null || accountIds === null) {
    return null;
  }
  if (orgIds.length === 0 && accountIds.length === 0) {
    return null;
  }
  if (orgIds.length + accountIds.length > CUSTOMER_COMPANY_LOOKUP_MAX_IDS) {
    return null;
  }
  return { org_ids: orgIds, account_ids: accountIds };
}
